// src/components/legal/TermsConditions.js
import * as React from "react";
import * as Accordion from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";

const Section = ({ value, title, children }) => (
  <Accordion.Item
    value={value}
    className="border-b border-gray-200 focus-within:bg-accent/10"
  >
    <Accordion.Header>
      <Accordion.Trigger className="group flex w-full items-center justify-between py-4 text-left text-base font-semibold outline-none">
        <span>{title}</span>
        <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
      </Accordion.Trigger>
    </Accordion.Header>
    <Accordion.Content className="pb-5 text-sm leading-6 text-gray-700 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
      <div className="space-y-3">{children}</div>
    </Accordion.Content>
  </Accordion.Item>
);

export default function TermsConditions() {
  return (
    // ✅ Added mt-24 (margin-top) to push content below the navbar
<main 
  className="container mx-auto max-w-3xl px-4 py-10" 
  style={{ paddingTop: "150px", background: "#ffffff" }}
>
      <h1 className="heading-1 mb-2">Terms &amp; Conditions</h1>
      <p className="body-small text-gray-600">Last updated: [December, 2025]</p>

      <div className="mt-6 rounded-xl border bg-white p-2 shadow-sm">
        <Accordion.Root type="single" collapsible className="w-full">
          <Section value="intro" title="1) Introduction & Definitions">
            <p>
              These Terms &amp; Conditions (&quot;Terms&quot;) apply to all
              offers, orders, and deliveries made by <strong>Magomeal</strong>
              (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;). By placing an
              order, the customer (&quot;you&quot;, &quot;customer&quot;) agrees
              to these Terms. Customers may be consumers (B2C) or businesses
              (B2B).
            </p>
            <p>
              Company details: Magomeal, Postbus 103, 5240 AC, Rosmalen, The
              Netherlands. Contact:{" "}
              <a href="mailto:info@magomeal.nl">info@magomeal.nl</a>.
            </p>
          </Section>

          <Section value="orders" title="2) Orders & Contract Formation">
            <ul className="list-disc pl-5">
              <li>
  Orders are placed through our online webshop. After an order is submitted, you will receive an order confirmation by email. A binding agreement is formed once payment has been successfully completed.
              </li>
              <li>
We reserve the right to refuse or cancel orders in cases such as pricing errors, stock inaccuracies, incomplete or incorrect customer information, or suspected fraud. If an order is cancelled after payment, a full refund will be issued.
              </li>
              <li>
If an item is out of stock or partially unavailable, we will inform you as soon as possible and propose an alternative product, partial delivery, or a revised delivery timeline.
              </li>
              <li>
Partial deliveries may be made where applicable.
              </li>
            </ul>
          </Section>

          <Section value="pricing" title="3) Prices, Currency & Payment">
            <ul className="list-disc pl-5">
              <li>
                All prices are listed in euros (€) and exclude VAT unless stated otherwise. Any applicable shipping costs are clearly displayed during checkout and in the order confirmation.
              </li>
              <li>
                Payment method:{" "}
                Payments are processed online via Mollie using the available payment methods, including iDEAL.
                .
              </li>
              <li>
                Orders are processed only after full payment has been successfully received. If a payment fails, is cancelled, or is reversed, the order may be delayed or cancelled.
              </li>
              <li>
                We reserve the right to correct obvious pricing errors and to adjust prices for future orders. Confirmed orders will be honored at the price shown at the time of purchase unless a clear error is identified.
              </li>
            </ul>
          </Section>

          <Section value="delivery" title="4) Delivery & Shipping">
            <ul className="list-disc pl-5">
              <li>We deliver within the Benelux region.</li>
              <li>
                Average delivery time: <strong>2–5 business days</strong> after
                payment. Carrier: <strong>PostNL</strong> or a comparable
                courier.
              </li>
              <li>
                Delivery timelines are estimates and may vary due to the
                carrier. We are not liable for delays caused by third parties.
              </li>
              <li>
                Risk transfers to you upon handover to the carrier (B2B) or upon
                delivery to your address (B2C), in line with applicable law.
              </li>
            </ul>
          </Section>

          <Section value="returns" title="5) Returns, Refunds & Consumer Rights">
            <ul className="list-disc pl-5">
              <li>
                <strong>Edible products</strong> are not returnable for hygiene
                and safety reasons once delivered, except if damaged or
                incorrect on arrival.
              </li>
              <li>
                Report issues within <strong>14 days</strong> of delivery (with
                order number, photos, and description). After verification, we
                will replace the item or issue a refund.
              </li>
              <li>
                This clause does not affect your statutory consumer rights under
                Dutch/EU law.
              </li>
            </ul>
          </Section>

          <Section
            value="condition"
            title="6) Product Condition, Shelf Life & Inspection"
          >
            <ul className="list-disc pl-5">
              <li>
                Edible cups have a shelf life indicated on the packaging. Follow
                storage instructions (cool, dry, away from direct sunlight).
              </li>
              <li>
                Inspect the delivery upon receipt. For visible defects or
                shortages, notify us promptly (within 14 days).
              </li>
              <li>
                We are not responsible for damage or deterioration caused by
                improper storage or handling after delivery.
              </li>
            </ul>
          </Section>

          <Section value="liability" title="7) Liability & Force Majeure">
            <ul className="list-disc pl-5">
              <li>
                Our total liability for any claim is limited to the amount paid
                for the product concerned.
              </li>
              <li>
                We are not liable for indirect or consequential losses (e.g.,
                loss of profit, business interruption).
              </li>
              <li>
                We are not liable for failure or delay caused by events beyond
                our reasonable control (force majeure), including but not
                limited to courier issues, strikes, accidents, natural events,
                or supplier delays.
              </li>
            </ul>
          </Section>

          <Section value="ip" title="8) Intellectual Property">
            <p>
              All content on our website (text, images, graphics, brand assets)
              is owned by or licensed to Magomeal and may not be copied,
              reproduced, or used without prior written permission.
            </p>
          </Section>

          <Section value="privacy" title="9) Privacy & Data Protection">
            <p>
              We handle personal data in line with our{" "}
              <a className="underline" href="/privacy-policy">
                Privacy Policy
              </a>{" "}
              and{" "}
              <a className="underline" href="/cookie-policy">
                Cookie Policy
              </a>
              . By ordering, you agree to the processing necessary to fulfil
              your order.
            </p>
          </Section>

          <Section value="changes" title="10) Changes to These Terms">
            <p>
              We may update these Terms from time to time. The latest version is
              published on this page and applies to new orders placed after the
              “Last updated” date.
            </p>
          </Section>

          <Section value="law" title="11) Governing Law & Disputes">
            <p>
              These Terms are governed by the laws of The Netherlands. Disputes
              will be submitted to the competent courts in The Netherlands,
              unless mandatory consumer law provides otherwise.
            </p>
          </Section>

          <Section value="misc" title="12) Miscellaneous">
            <ul className="list-disc pl-5">
              <li>
                If any provision of these Terms is held invalid or
                unenforceable, the remaining provisions remain in full force.
              </li>
              <li>
                These Terms, together with our email order confirmation and
                invoice, form the entire agreement between you and Magomeal for
                the relevant order.
              </li>
              <li>
                For questions, contact{" "}
                <a href="mailto:info@magomeal.nl">info@magomeal.nl</a>.
              </li>
            </ul>
          </Section>
        </Accordion.Root>
      </div>
    </main>
  );
}
